<template>
  <hypto-popup :title="title" :footer="false" css="width-lg">
    <template v-slot:body>
      <slot name="actions" />

      <div v-if="btnOptions.length === 0" class="actions">
        <button class="primary-btn" v-if="txnDetails.webhook_allowed" @click="resendWebhook">
          Resend Webhook
          <i class="fa fa-paper-plane" />
        </button>
        <button class="dark-btn" v-if="txnDetails.email_allowed" @click="resendEmail">
          Resend Email
          <i class="fa fa-envelope" />
        </button>
      </div>

      <div v-else class="actions">
        <button
          v-for="(btn, index) in btnOptions"
          :key="index"
          :class="btn.css"
          @click="btn.action"
        >
          {{btn.text}}
          <i class="fa" :class="btn.icon" />
        </button>
      </div>

      <div v-for="(txn, index) in txnDetails.popup" :key="index" class="txn-row">
        <h6>{{txn.field}}</h6>

        <p>
          <u v-if="txn.route || txn.external" @click="redirect(txn, $event)">{{txn.value}}</u>
          <span v-else>{{txn.value}}</span>

          <i v-if="['Status', 'Refund Status'].includes(txn.field) && txn.value === 'PENDING'"
              class="fa fa-sync-alt" @click="pendingStatus($event)" />
          <i class="fa fa-info-circle" v-else-if="txn.tooltip">
            <md-tooltip md-direction="top">{{txn.tooltip}}</md-tooltip>
          </i>
        </p>
      </div>
    </template>
  </hypto-popup>
</template>

<script>
import HyptoPopup from '@/components/global/HyptoPopup'

import ApiServices from '@/services/api/accountStatement'
import { openPopup } from '@/utils/helpers'

export default {
  name: 'StatementDetails',
  components: { HyptoPopup },
  props: {
    txnDetails: Object,
    title: {
      type: String,
      default: 'Transaction Details'
    },
    btnOptions: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    isConnectedBanking () {
      const connectedBanking = this.txnDetails.popup.find(e => e.field === 'Connected Banking')
      if (connectedBanking) {
        return connectedBanking.value === 'Yes'
      }
      return this.txnDetails.txn_type === 'Received - Connected VA'
    }
  },
  watch: {
    txnDetails: {
      handler: 'populateData',
      immediate: true
    }
  },
  methods: {
    openPopup,
    populateData () {
      this.txnDetails.popup = this.txnDetails.popup.map(e => {
        switch (e.field) {
          case 'Hypto Virtual Account ID':
            e.route = {name: 'VATransactions', params: {id: e.value}}
            break
          case 'Hypto UPI ID':
            e.route = {name: 'UPITransactions', params: {id: e.value}}
            break
          case 'Reference Number':
            if (['UPI Collection - Payment', 'UPI Collection - Refund', 'Refund - UPI Collection Refund'].includes(this.txnDetails.txn_type)) {
              e.route = {name: 'UPICollections', query: {q: encodeURIComponent(e.value)}}
            }
            break
          case 'Order ID':
            if (this.txnDetails.txn_type === 'UPI Collection - Payment') {
              e.route = {name: 'UPICollections', query: {q: encodeURIComponent(e.value)}}
            }
            break
          case 'Transaction Amount':
            if (this.isConnectedBanking && ['UPI Collection - Payment', 'Received - Connected VA', 'Received - Connected UPI'].includes(this.txnDetails.txn_type)) {
              e.tooltip = 'This amount will not be credited to your Hypto Account'
            }
            break
          case 'Settlement Amount':
            if (this.isConnectedBanking && this.txnDetails.txn_type === 'Bank Transfer') {
              e.tooltip = 'This amount was not debited from your Hypto Account'
            }
            break
          case 'Verification Account IFSC':
            if (this.txnDetails.initiated_account_ifsc !== '-') {
              e.tooltip = `Due to Bank Merger, account verification has been initiated to ${this.txnDetails.initiated_account_ifsc}`
            }
            break
          case 'Bank Account IFSC':
            if (this.txnDetails.initiated_account_ifsc !== '-') {
              e.tooltip = `Due to Bank Merger, payout has been initiated to ${this.txnDetails.initiated_account_ifsc}`
            }
            break
          case 'Settler Virtual Account ID':
            if (e.value !== '-') {
              e.route = {name: 'VATransactions', params: {id: e.value}}
            }
            break
          case 'Payment Link':
            e.external = e.value
            break
        }
        return e
      })
    },
    redirect (txn, $event) {
      if ($event.ctrlKey || $event.metaKey) {
        const ROUTE_DATA = txn.external || this.$router.resolve(txn.route).href
        window.open(ROUTE_DATA, '_blank')
      } else {
        if (txn.external) {
          window.open(txn.external, '_blank')
        } else if (this.$route.name !== 'VAIdInvalidCredits') {
          this.$router.push(txn.route)
        }
        this.openPopup(null)
      }
    },
    pendingStatus (e) {
      const id = this.txnDetails.id
      const status = this.txnDetails.status
      this.$emit('refreshList', e, {id, status}, true)
    },
    resendWebhook () {
      ApiServices.resendWebhook(this.txnDetails.id).then(response => {
        this.handleToast(response)
      })
    },
    resendEmail () {
      ApiServices.resendEmail(this.txnDetails.id).then(response => {
        this.handleToast(response)
      })
    },
    handleToast (response) {
      response.success ? this.successToastMsg(response.message) : this.errorToastMsg(response.message)
    }
  }
}
</script>

<style scoped lang="scss">
  @import '@/assets/scss/mixins.scss';

  .actions {
    button {
      margin-bottom: 10px;
    }
    i {
      margin-left: 10px;
    }
  }

  .txn-row {
    display: flex;
    margin: 5px 0;

    h6 {
      @include normal-text($grey-text, 13px, 500);
      width: 45%;
      text-align: left;
      margin-right: 10px;
    }
    p {
      @include highlight-text($secondary, 13px);
      text-align: left;
      word-break: break-word;

      u {
        cursor: pointer;
        color: $primary;
      }
    }

    .fa-sync-alt, .fa-info-circle {
      color: $primary;
      margin: 0 5px;
      cursor: pointer;
    }
  }

  .desktop {
    .actions {
      text-align: left;

      button {
        min-width: 170px;
        margin-right: 10px;
      }
    }

    .txn-row {
      p {
        width: 45%;
      }
    }
  }

  .mobile, .tablet {
    .actions {
      @include custom-flex(space-between, null);

      button {
        flex: 1;
        max-width: 48%;
      }
    }

    .txn-row {
      p {
        flex: 1;
      }
    }
  }

  .mobile {
    .actions button {
      font-size: 12px;
      padding: 10px 5px !important;
    }
  }
</style>
